/*  Imports from Redux:
 applyMiddleware: Applies middleware to the dispatch method of the Redux store
 combineReducers: Merges reducers into one
 createStore: Creates a Redux store that holds the state tree
 Store: The TS Type used for the store, or state tree
 */
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import createFilter from "redux-persist-transform-filter";

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import { aiReducer } from "./slices/ai";
import { localSettingsReducer } from "./slices/appSettings";
import { audienceReducer } from "./slices/audiences";
import { dashboardFiltersReducer } from "./slices/dashboardFilters";
import { exclusionGroupsReducer } from "./slices/exclusionGroups";
import { experienceProductsReducer } from "./slices/experienceProducts";
import { experienceReducer } from "./slices/experiences";
/*  Thunk
Redux Thunk middleware allows you to write action creators that return a function instead of an action. The thunk can be used to delay the dispatch of an action, or to dispatch only if a certain condition is met. The inner function receives the store methods dispatch and getState as parameters.
*/
import { globalReducer } from "./slices/global";
import { offerProductsReducer } from "./slices/offerProducts";
import { offersReducer } from "./slices/offers";
import { onsiteEditsReducer } from "./slices/onsiteEdits";
import { onsiteInjectionsReducer } from "./slices/onsiteInjections";
import { pageTargetingReducer } from "./slices/pageTargeting";
import { personalizationStateReducer } from "./slices/personalizationState";
import { productPickerReducer } from "./slices/productPicker";
import { redirectsReducer } from "./slices/redirects";
import { sitewideDashboardFiltersReducer } from "./slices/sitewideDashboardFilters";
import { userInterfacesReducer } from "./slices/userInterfaces";
import { variationsReducer } from "./slices/variations";
import { widgetsReducer } from "./slices/widgets";
import { LoggedOrg, PerformanceView } from "./types/appSettings";
// import createFilter from "redux-persist-transform-filter";

// Copied form node_modules/@sentry/react/dist/redux.d.ts
// Sentry does not export these interfaces for some reason
interface Action<T = any> {
  type: T;
}

interface AnyAction extends Action {
  [extraProps: string]: any;
}
const appSettingsFilter = createFilter("appSettings", [
  "isShopLogin",
  "snoozeShopifyScopesDialog",
  "loggedOrgs",
  "dismissedAlerts",
  "rememberEmail",
  "onboardingPage",
  "showDuplicateProductIntro",
  "viewedAnalyticsDialog",
  "viewedInfoPopup",
  "performanceViews",
  "showIds",
  "useWideApp",
  "sideNavCollapsed",
  "viewedCampaignPopup",
]);

const persistConfig: PersistConfig<any> = {
  key: "root",
  storage,
  whitelist: ["appSettings"],
  transforms: [appSettingsFilter],
};

// Create the root SharedReducer
const rootReducer = combineReducers({
  global: globalReducer!,
  appSettings: localSettingsReducer,
  dashboardFilters: dashboardFiltersReducer,
  sitewideDashboardFilters: sitewideDashboardFiltersReducer,
  offers: offersReducer,
  offerProducts: offerProductsReducer,
  redirects: redirectsReducer,
  widgets: widgetsReducer,
  userInterfaces: userInterfacesReducer,
  onsiteInjections: onsiteInjectionsReducer,
  pageTargeting: pageTargetingReducer,
  ai: aiReducer,
  exclusionGroups: exclusionGroupsReducer,
  onsiteEdits: onsiteEditsReducer,
  experienceProducts: experienceProductsReducer,
  variations: variationsReducer,
  experience: experienceReducer,
  audience: audienceReducer,
  productPicker: productPickerReducer,
  personalizationState: personalizationStateReducer,
});

// @ts-ignore
const persistedReducer = persistReducer(persistConfig, rootReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer(action: AnyAction): AnyAction | null {
    // Add any additional types to ignore entirely
    // ex format: api/callSuccess
    const ignoredTypes: string[] = [];
    if (ignoredTypes.includes(action.type)) {
      return null;
    }

    if (action.token) {
      return { ...action, token: null };
    }
    return action;
  },
  stateTransformer(state: any | undefined): any {
    // add other checks for any other sensitive data
    return state;
  },

  // This looks useful! Not sure where to use though.

  // Called on every state update, configure the Sentry Scope with the Redux state.
  // The first parameter is the scope, which is the same scope instance that you would get
  // when you call Sentry.configureScope, and the second parameter is the latest Redux state.

  // configureScopeWithState: (scope, state) => {
  //   // Set tag if the user is using imperial units.
  //   if (state.settings.useImperialUnits) {
  //     scope.setTag("user.usesImperialUnits", true);
  //   }
  // },
});

// Create a configure store function of type `IAppState`
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "experiences/updatedSelectedExperimentDashboardSettings",
        ],
      },
    }),
  devTools: true,
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat([sentryReduxEnhancer]),
});

export const persistor = persistStore(store);

export default store;
